import { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import RecommenderPage from "./pages/RecommenderPage/RecommenderPage";
import MentorPal from "./pages/Mentorpal/MentorPal";
import Transition from "./pages/Transition/Transition";
import { ContentPage } from "./pages/ContentPage/ContentPage";
import { Provider } from "react-redux";
import { store, useAppDispatch, useAppSelector } from "./app/store";
import { RouteTracker } from "./hooks/use-with-route-logging";
import { useWithLtikSession } from "./hooks/use-with-ltik-session";
import { hydrateResourceBookmarks } from "./stores/bookmarks/resourceBookmarksSlice";
import { hydrateMentorBookmarks } from "./stores/bookmarks/mentorBookmarksSlice";
import { ToastHost } from "./components/NotificationHost/NotificationHost";
import { hydrateMentorRecommendations } from "./stores/recommendations/mentorRecommendationsSlice";
import { RedirectControlSurvey } from "./pages/RedirectControlSurvey";
import { FinishedControlSurvey } from "./pages/FinishedControlSurvey";
import { AccessCodeEntry } from "./components/AccessCode/AccessCodeEntry";
import { useAccessGate } from "./hooks/use-access-gate";
import { setAccessCode, startNewSession } from "./stores/sessionData/sessionDataSlice";
import { LoadingGate } from "./components/LoadingGate/LoadingGate";
import { hydrateUser } from "./stores/user/userSlice";
import { requireEnv } from "./api";
import { useUserObjectExists } from "./hooks/use-user-object-exists";

export const REDIRECT_CONTROL_SURVEY_ROUTE = "/redirect-control-survey";
export const COMPLETED_CONTROL_SURVEY_ROUTE = "/completed-control-survey";
const REDIRECT_URL = requireEnv("REACT_APP_PRE_SURVEY_URL");

function App() {
  return (
    <Provider store={store}>
      <ToastHost />
      <AppBootstrap />
    </Provider>
  );
}

function AppBootstrap() {
  const dispatch = useAppDispatch();

  const { gradePosted, submittingGrade, isLtikSession, canvasId } =
    useWithLtikSession();

  const { accessCode, userId, sessionId } = useAppSelector((state) => state.session);
  const isAtRedirectControlSurveyRoute = window.location.pathname.includes(REDIRECT_CONTROL_SURVEY_ROUTE);
  const isAtCompletedControlSurveyRoute = window.location.pathname.includes(COMPLETED_CONTROL_SURVEY_ROUTE);
  const handleSetAccessCode = (code: string | null) => {
    dispatch(setAccessCode(code));
  };

  const userData = useAppSelector((state) => state.user.data);
  const isPreTestSurveyComplete =
    userData?.status.isPreTestSurveyComplete ?? false;
  const isPostTestSurveyComplete =
    userData?.status.isPostTestSurveyComplete ?? false;
  
  const { gateState, gateError, submitCode } = useAccessGate({
    accessCode,
    setAccessCode: handleSetAccessCode,
  });

  const { userBootstrapState } = useUserObjectExists(userId ?? null, gateState === "unlocked");
  
  // if no user ID found, redirect user to get userId
  useEffect(() => {
    if (userId && userId.startsWith("Pathways")) return;
    if (isAtRedirectControlSurveyRoute || isAtCompletedControlSurveyRoute) return;
    window.location.replace(`${REDIRECT_URL}?canvasid=${canvasId}`);
  }, [userId, canvasId, isAtRedirectControlSurveyRoute, isAtCompletedControlSurveyRoute]);

  // if no session ID found, start new session
  useEffect(() => {
    if (!userId) return;
    if (sessionId) return;

    dispatch(startNewSession());
  }, [dispatch, userId, sessionId]);

  // hydrate slices once the userId and user object exists
  useEffect(() => {
    if (!userId) return;
    if (userBootstrapState !== "ready") return;

    dispatch(hydrateUser(userId));
    dispatch(hydrateResourceBookmarks(userId));
    dispatch(hydrateMentorBookmarks(userId));
    dispatch(hydrateMentorRecommendations(userId));
  }, [dispatch, userId, userBootstrapState]);

  if(isAtRedirectControlSurveyRoute || isAtCompletedControlSurveyRoute) {
    return (
      <Router>
      <RouteTracker />
      <Routes>
        <Route path={REDIRECT_CONTROL_SURVEY_ROUTE} element={<RedirectControlSurvey canvasId={canvasId} />} />
        <Route path={COMPLETED_CONTROL_SURVEY_ROUTE} element={<FinishedControlSurvey submittingGrade={submittingGrade} gradePosted={gradePosted} />} />
      </Routes>
    </Router>
    );
  }


  if (!userId) {
    return (
      <LoadingGate 
        title="Redirecting to login..." 
        subtitle="We could not find your user information. Please wait while we send you to the login form." 
      />
    );
  }

  if (gateState === "bootstrapping") {
    return (
      <LoadingGate 
        title="Checking access..." 
        subtitle="Please wait while we verify your access code." 
      />
    );
  }

  if (gateState !== "unlocked") {
    return (
      <AccessCodeEntry onSubmit={submitCode} error={gateError} isSubmitting={gateState === "submitting"} />
    );
  }

  if (!accessCode) {
    return (
      <AccessCodeEntry onSubmit={handleSetAccessCode} />
    )
  }

  if (userBootstrapState === "polling" || !userData) {
    return (
      <LoadingGate
        title="Preparing your account..."
        subtitle={
          <>
            Please wait while we finish signing you in. If it is taking too long, try{" "} 
            <a href={`${REDIRECT_URL}?canvasid=${canvasId}`}>
              signing in again
            </a>
            .
          </>
        }
      />
    );
  }

  return (
    <Router>
      <RouteTracker />
      <Routes>
        {/* Protected Routes */}
        <Route path="/" element={<ContentPage completedPreSurvey={isPreTestSurveyComplete} isLtikSession={isLtikSession} />} />
        <Route path="/mentorpal" element={<MentorPal completedPostSurvey={isPostTestSurveyComplete} />} />
        <Route path="/transition" element={<Transition />} />
        <Route path="/recommenderpage/*" element={<RecommenderPage gradePosted={gradePosted} submittingGrade={submittingGrade} completedPostSurvey={isPostTestSurveyComplete} />} />
      </Routes>
    </Router>
  );
}

export default App;
